import { create } from 'zustand';
import { clientArticlesApi } from '@api/api-config.ts';
import {
  ClientUnreadArticleResponse,
  ClientArticleDetailResponse,
  ClientArticleListResponse,
} from '@api/generated/search-service';

interface PaginationState {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface ArticlesStoreState {
  // Nepřečtené články
  unreadArticles: ClientUnreadArticleResponse[];
  unreadCount: number;
  urgentArticles: ClientUnreadArticleResponse[];

  // Seznam článků (stránkovaný)
  articles: ClientArticleListResponse[];
  articlesLoading: boolean;
  articlesPagination: PaginationState | null;

  // Detail článku pro modal
  currentArticle: ClientArticleDetailResponse | null;
  currentArticleLoading: boolean;
  showArticleModal: boolean;

  // Urgentní modal (blokující)
  showUrgentModal: boolean;
  currentUrgentArticle: ClientUnreadArticleResponse | null;

  // Akce
  fetchUnreadArticles: (organizationId: number, portalId: number) => Promise<void>;
  fetchArticles: (organizationId: number, portalId: number, page?: number, size?: number) => Promise<void>;
  fetchArticleDetail: (organizationId: number, portalId: number, articleId: number) => Promise<void>;
  openArticleModal: (organizationId: number, portalId: number, articleId: number) => Promise<void>;
  closeArticleModal: () => void;
  closeUrgentModal: () => void;
  processUrgentArticles: () => void;
  reset: () => void;
}

export const useArticlesStore = create<ArticlesStoreState>((set, get) => ({
  // Nepřečtené články
  unreadArticles: [],
  unreadCount: 0,
  urgentArticles: [],

  // Seznam článků
  articles: [],
  articlesLoading: false,
  articlesPagination: null,

  // Detail článku
  currentArticle: null,
  currentArticleLoading: false,
  showArticleModal: false,

  // Urgentní modal
  showUrgentModal: false,
  currentUrgentArticle: null,

  fetchUnreadArticles: async (organizationId: number, portalId: number) => {
    try {
      const response = await clientArticlesApi.getClientUnreadArticles({ organizationId, portalId });
      const unreadArticles = response.data;
      const urgentArticles = unreadArticles.filter(a => a.isUrgent);

      set({
        unreadArticles,
        unreadCount: unreadArticles.length,
        urgentArticles,
      });

      // Automaticky zpracuj urgentní články
      get().processUrgentArticles();
    } catch (error) {
      console.error('Chyba při načítání nepřečtených článků:', error);
    }
  },

  fetchArticles: async (organizationId: number, portalId: number, page = 1, size = 20) => {
    try {
      set({ articlesLoading: true });
      const response = await clientArticlesApi.getClientArticles({ organizationId, portalId, page, size });
      set({
        articlesLoading: false,
        articles: response.data.items,
        articlesPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({ articlesLoading: false });
      console.error('Chyba při načítání článků:', error);
    }
  },

  fetchArticleDetail: async (organizationId: number, portalId: number, articleId: number) => {
    try {
      set({ currentArticleLoading: true });
      const response = await clientArticlesApi.getClientArticleDetail({ organizationId, portalId, articleId });
      set({
        currentArticleLoading: false,
        currentArticle: response.data,
      });

      // Po přečtení článku aktualizuj stav
      const { unreadArticles, urgentArticles, articles } = get();
      const newUnreadArticles = unreadArticles.filter(a => a.articleId !== articleId);
      set({
        // Odeber z nepřečtených
        unreadArticles: newUnreadArticles,
        unreadCount: newUnreadArticles.length,
        urgentArticles: urgentArticles.filter(a => a.articleId !== articleId),
        // Aktualizuj stav v seznamu článků
        articles: articles.map(a => (a.articleId === articleId ? { ...a, isRead: true } : a)),
      });
    } catch (error) {
      set({ currentArticleLoading: false });
      console.error('Chyba při načítání detailu článku:', error);
    }
  },

  openArticleModal: async (organizationId: number, portalId: number, articleId: number) => {
    set({ showArticleModal: true });
    await get().fetchArticleDetail(organizationId, portalId, articleId);
  },

  closeArticleModal: () => {
    set({
      showArticleModal: false,
      currentArticle: null,
    });
  },

  closeUrgentModal: () => {
    set({
      showUrgentModal: false,
      currentUrgentArticle: null,
    });

    // Pokud jsou další urgentní články, zobraz další
    get().processUrgentArticles();
  },

  processUrgentArticles: () => {
    const { urgentArticles, showUrgentModal } = get();

    // Pokud už je modal otevřený nebo nejsou urgentní články, nic nedělej
    if (showUrgentModal || urgentArticles.length === 0) {
      return;
    }

    // Zobraz první urgentní článek
    const firstUrgent = urgentArticles[0];
    set({
      showUrgentModal: true,
      currentUrgentArticle: firstUrgent,
    });
  },

  reset: () => {
    set({
      unreadArticles: [],
      unreadCount: 0,
      urgentArticles: [],
      articles: [],
      articlesLoading: false,
      articlesPagination: null,
      currentArticle: null,
      currentArticleLoading: false,
      showArticleModal: false,
      showUrgentModal: false,
      currentUrgentArticle: null,
    });
  },
}));
